import { mapActions, mapGetters, mapState } from 'vuex'
import { formatCurrency } from '@utils/currency-helpers'

export default {
  props: {
    rate: {
      type: Object,
      required: true
    },
    room: {
      type: Object,
      required: true
    },
    bedType: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    isLoading: false
  }),
  computed: {
    ...mapGetters('i18n', ['tc']),
    ...mapGetters('cart', ['itemsInCart', 'bedsIds']),
    ...mapState('cart', ['cartPending']),
    ...mapState('booking', ['query']),
    ...mapState('modifyReservation', ['isBookingEditMode', 'selectedReservations']),
    ...mapGetters('booking', ['getRangeInDays', 'currentRoom', 'getCurrentRoomId']),
    ...mapGetters('currency', ['getDisplayPrice']),
    isSelected() {
      const hasBedTypeTitle = !!this.currentRoom?.sitecoreContent?.bed?.title && this.bedType?.title
      const currentRoomRatePlanCode = this.currentRoom?.ratePlanCode
      if (hasBedTypeTitle) {
        return currentRoomRatePlanCode === this.ratePlanCode && `${this.currentRoom?.bedTypeCode}-${this.currentRoom?.sitecoreContent?.bed?.title}` === `${this.bedTypeCode}-${this.bedType.title}`
      }
      return currentRoomRatePlanCode === this.ratePlanCode && this.currentRoom?.bedTypeCode === this.bedTypeCode
    },
    isRoomSelected() {
      return this.currentRoom?.bedTypeCode === this.roomTypeCode
    },
    bedTypeHasChanged() {
      return this.currentRoom?.bedTypeCode !== this.bedTypeCode
    },
    roomTypeCode() {
      return this.room.synXisID
    },
    ratePlanCode() {
      return this.rate.synXisID
    },
    bedTypeCode() {
      return this.bedType.synXisID
    },
    bedsInCart() {
      return this.bedsIds.filter((b) => b === this.bedType?.synXisID).length
    },
    rateImg() {
      return this.rate.imageAltText ? { src: this.rate.image, alt: this.rate.imageAltText } : { src: 'https://photos.mandarinoriental.com/is/image/MandarinOriental/guangzhou-event-venue-detail-7', alt: this.rate.title }
    },
    totalPrice() {
      return this.rate.nightlyRates.map((n) => n.price).reduce((a, b) => a + b, 0)
    },
    totalPriceWithTaxes() {
      return this.rate.nightlyRates.map((n) => n.price + n.fees + n.taxes).reduce((a, b) => a + b, 0)
    },
    avgPrice() {
      return this.getDisplayPrice({ price: this.totalPrice / this.getRangeInDays, withIsoCode: true, isoAfter: true })
    },
    avgPriceWithTaxes() {
      return this.getDisplayPrice({ price: this.totalPriceWithTaxes / this.getRangeInDays, withIsoCode: true, isoAfter: true })
    },
    numberOfUnits() {
      return this.bedType && (this.bedType.numberOfUnits - this.bedsInCart)
    },
    isDisabled() {
      return this.cartPending || (!this.numberOfUnits && !this.isRoomSelected)
    }
  },
  methods: {
    ...mapActions('cart', {
      addToCartAction: 'addToCart',
      updateCartLineAction: 'updateCartLine',
      clearEnhancementsAction: 'clearEnhancementsFromRooms'
    }),
    ...mapActions('cart', ['resetRoomUpgrades']),
    doCartAction() {
      if (this.isSelected) {
        if (this.bedTypeHasChanged) {
          this.updateCartLine(this.isSelected)
        } else {
          this.continue()
        }
      } else if (this.currentRoom) {
        this.updateCartLine()
      } else {
        this.addToCart()
      }
    },
    async addToCart() {
      this.isLoading = true
      const itemToAdd = this.createCartItem()
      if (this.isBookingEditMode) {
        itemToAdd.confirmationNumber = this.selectedReservations[this.getCurrentRoomId].id
      }
      this.$logger.debug('adding to cart: ', itemToAdd)
      this.$track({ event: 'cartAdd' })
      try {
        await this.addToCartAction({ item: itemToAdd })
      } catch (err) {
        this.$logger.error(err)
      }
      this.isLoading = false
      await this.clearEnhancementsAction()
    },
    async updateCartLine(isSelected = false) {
      if (this.bedsIds.length === 1 && this.bedsIds[0] !== this.bedTypeCode) {
        this.resetRoomUpgrades()
      }
      this.isLoading = true
      const itemToAdd = this.createCartItem()
      if (this.isBookingEditMode) {
        itemToAdd.confirmationNumber = this.selectedReservations[this.getCurrentRoomId].id
      }
      this.$logger.debug('updating cart line: ', itemToAdd)
      try {
        await this.updateCartLineAction(itemToAdd, isSelected)
      } catch (err) {
        this.$logger.error(err)
      }
      this.isLoading = false
    },
    async continue() {
      this.$emit('continue')
    },
    createCartItem() {
      return {
        ...this.query,
        roomTypeCode: this.roomTypeCode,
        ratePlanCode: this.ratePlanCode,
        bedTypeCode: this.bedTypeCode,
        bedTypeDescription: this.bedType.addItemNameComment ? this.bedType.name : '',
        fees: this.rate.fees,
        taxes: this.rate.taxes,
        total: this.rate.total,
        taxesFees: this.rate.taxesFees,
        nightlyRates: this.rate.nightlyRates,
        sitecoreContent: {
          room: {
            title: this.room.title,
            description: this.room.shortDescription,
            image: this.room.images[0]?.src
          },
          rate: {
            title: this.rate.title,
            description: this.rate.description
          },
          bed: {
            title: this.bedType.title,
            description: this.bedType.description
          }
        },
        restrictionPolicy: this.rate.restrictionPolicy,
        upgradeRoom: this.bedType.upgradeRoom,
        upgradeSelected: false,
        upgradePriceDifferencePerNight: 0,
        numberOfUnits: this.numberOfUnits,
        maxGuests: this.room.maxGuests,
        guaranteeCode: this.rate.guaranteeCode,
      }
    }
  },
  filters: {
    currencyFilter(number) {
      return formatCurrency(number)
    }
  }
}
